import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRequest, patchRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';

const getImageUrl = (image) => {
  if (!image) return null;
  if (/^https?:\/\//i.test(image)) return image;

  const backendBase = (process.env.REACT_APP_BACKEND || '').replace(/\/+$/, '');
  if (!backendBase) return image;

  const normalizedPath = String(image).replace(/^\/+/, '');
  const fullPath = normalizedPath.startsWith('uploads/')
    ? normalizedPath
    : `uploads/${normalizedPath}`;
  return `${backendBase}/${fullPath}`;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const UpdateLeadership = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [current, setCurrent] = useState(null);
  const [formData, setFormData] = useState({
    heading: '',
    title: '',
    description: '',
    image: null,
    status: 'active',
  });

  useEffect(() => {
    const fetchLeader = async () => {
      setFetching(true);
      try {
        const res = await getRequest(`leadership/${id}`);
        if (res?.data?.success) {
          const item = res.data.data;
          setCurrent(item);
          setFormData({
            heading: item.heading || '',
            title: item.title || '',
            description: item.description || '',
            image: null,
            status: item.status || 'active',
          });
        } else {
          toast.error(res?.data?.message || 'Failed to load leader');
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Error fetching leader');
      } finally {
        setFetching(false);
      }
    };

    if (id) fetchLeader();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (e.target.type === 'file') {
      setFormData((prev) => ({
        ...prev,
        [name]: files && files.length > 0 ? files[0] : null,
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.heading.trim()) {
      toast.error('Heading is required');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    setLoading(true);

    const payload = new FormData();
    payload.append('heading', formData.heading.trim());
    payload.append('title', formData.title.trim());
    payload.append('description', formData.description.trim());
    payload.append('status', formData.status);

    if (formData.image) {
      payload.append('image', formData.image);
    }

    try {
      const response = await patchRequest(`leadership/${id}`, payload, getAuthHeaders());

      if (response?.data?.success) {
        toast.success('Leader updated successfully');
        navigate('/leadership');
      } else {
        toast.error(response?.data?.message || 'Update failed');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error updating leader');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div className="text-center mt-5">Loading leader...</div>;

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4">
        <h4 className="font-weight-bold">Update Leader</h4>
      </div>

      <form onSubmit={handleSubmit} className="form-style">
        <div className="row mx-4 mt-5">
          <div className="col-md-6 mt-2">
            <label htmlFor="heading" className="lables">Heading *</label>
            <input
              id="heading"
              name="heading"
              className="form-control"
              value={formData.heading}
              onChange={handleChange}
              required
            />
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="title" className="lables">Title *</label>
            <input
              id="title"
              name="title"
              className="form-control"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="image" className="lables">Image (Leave blank to keep current)</label>
            <input
              id="image"
              name="image"
              type="file"
              className="form-control-file"
              accept=".png,.jpg,.jpeg,.webp,.gif,.bmp,.svg,.avif,.tiff,.ico,.jfif,.pjpeg,.pjp,image/png,image/jpeg,image/jpg,image/webp,image/gif,image/bmp,image/svg+xml,image/avif,image/tiff,image/x-icon,image/jfif,image/*"
              onChange={handleChange}
            />
            {formData.image && (
              <small className="text-muted d-block mt-1">Selected: {formData.image.name}</small>
            )}
            {!formData.image && current?.image && (
              <div className="mt-2">
                <img
                  src={getImageUrl(current.image)}
                  alt={current.heading}
                  width={120}
                  style={{ borderRadius: '4px', objectFit: 'cover' }}
                />
              </div>
            )}
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="status" className="lables">Status</label>
            <select
              id="status"
              name="status"
              className="form-control"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="col-12 mt-2">
            <label htmlFor="description" className="lables">Description *</label>
            <textarea
              id="description"
              name="description"
              rows="5"
              className="form-control"
              value={formData.description}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="mt-5 text-right mx-4">
          <button type="submit" className="btn btn-lg addbtn" disabled={loading}>
            {loading ? 'Updating...' : 'Update Leader'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateLeadership;
